/**
 * TwinViz -- SVG renderer for the constellation, all 3 directions.
 *
 * Pure presentation over the model. Takes:
 *   mode      -- which layout to render (observatory / flow / orbit)
 *   nodes     -- TwinGraph.nodes
 *   edges     -- TwinGraph.edges
 *   states    -- per-node visual state map (from nodeState.ts)
 *   selected  -- the inspected node id (or null)
 *   blastSet  -- downstream-reachable set when a node is selected
 *   showBlast -- toggle from the deck; when false, blast lighting off
 *   onSelect  -- click handler
 *   onHover/onLeave -- tooltip drivers
 *
 * Ported from the prototype's twin-viz.jsx, adapted to v2's
 * TwinGraph/TwinEdge contracts.
 */
import { i18n } from "shared/i18n";
import { useCallback, useEffect, useMemo } from "react";

import {
  KIND_LABEL,
  STATE_FILL,
  STATE_STROKE,
  type NodeVisualState,
  nodeRadius,
} from "twin/constellation/nodeState";
import {
  VIEW_H,
  VIEW_W,
  flowColumns,
  flowLayout,
  observatoryLayout,
  orbitLayout,
  serviceRingLayout,
  type LayoutResult,
  type NodePos,
} from "twin/constellation/layouts";
import type { TwinEdge, TwinNode, TwinNodeKind } from "shared/api/types";

import { bundledPath, computeTrunks } from "./bundling";
import { labelBox, nodeAtPoint, nodeHitBox } from "./hitArea";
import type { HitBox } from "./hitArea";
import { estimateLabelWidth, selectVisibleLabels } from "./spacing";

export type ConstellationMode = "observatory" | "flow" | "orbit" | "ring";

/** A node's role in a bidirectional trace. "both" means the node sits
 * on a CYCLE with the seed -- consequence and prerequisite at once. */
export type TraceRole = "downstream" | "upstream" | "both";

// P197 live-telemetry lens: per-status ring tone for service nodes.
export type LiveStatus = "healthy" | "degraded" | "down" | "unknown";
const LIVE_RING: Record<LiveStatus, { stroke: string; glow?: string }> = {
  down: { stroke: "#e2574a", glow: "url(#cstGlowRed)" },
  degraded: { stroke: "#e0a93a", glow: "url(#cstGlowBrass)" },
  healthy: { stroke: "#34d399" },
  unknown: { stroke: "#94a3b8" },
};

export interface TwinVizProps {
  mode: ConstellationMode;
  /** Coordinate-space width, matched to the stage aspect by the
   * parent so the scene fills the canvas (defaults to the design
   * width). */
  viewW?: number;
  nodes: TwinNode[];
  edges: TwinEdge[];
  states: Map<string, NodeVisualState>;
  /** When non-null a search is active: these node ids are matches
   * to highlight; everything else dims. */
  matchSet?: Set<string> | null;
  selected: string | null;
  blastSet: Set<string> | null;
  showBlast: boolean;
  /** P167 fragility lens: per-node {ratio 0..1 of max score, stress}.
   * When non-null, scored nodes get a fragility aura. */
  fragility?: Map<string, { ratio: number; stress: boolean }> | null;
  /** P385 bidirectional trace: hop depth per node in each direction
   * from the selected seed. Downstream is CONSEQUENCE, upstream is
   * PREREQUISITE -- rendered distinctly on purpose. */
  trace?: { downstream: Map<string, number>; upstream: Map<string, number> } | null;
  /** Hops currently lit (Infinity = fully revealed). */
  revealDepth?: number;
  /** P385 ring mode: admit services with no modelled dependency onto a
   * trailing arc. Off by default -- only 37 of 78 services carry a
   * dependency edge on the live estate. */
  includeIsolated?: boolean;
  /** Round 11 C1 -- viewBox units at the bottom-start corner the scene
   *  must not draw in, because the application's floating queue panel
   *  is pinned over them. Zero when there is no panel. */
  reserveBottomStart?: number;
  /** ... and the bottom-end corner, where the inspector opens. */
  reserveBottomEnd?: number;
  /**
   * Round 12 E4 -- how many nodes are drawn with no name on them.
   *
   * Labels that would collide are dropped rather than overprinted
   * (P404), which is right, and on the live estate it silently removes
   * seventeen of seventy-eight names. The walk counted twenty and wrote
   * it down as a defect, reasonably: a scene that draws a node and
   * declines to name it, with nothing saying so, reads as a scene that
   * does not know what it is drawing. Reported so the view can say it.
   */
  onLabelBudget?: (hidden: number) => void;
  /** P197 live-telemetry lens: per-SERVICE current health status. When
   * non-null, service nodes get a status ring (down/degraded pulse). */
  liveHealth?: Map<string, LiveStatus> | null;
  onSelect: (id: string) => void;
  onHover: (
    node: TwinNode,
    event: React.MouseEvent<SVGGElement>,
    state: NodeVisualState,
  ) => void;
  onLeave: () => void;
}

const COL_HEAD_LABELS: { kind: TwinNodeKind; label: string }[] = [
  { kind: "channel", label: "Channels" },
  { kind: "ibs", label: "Important Business Services" },
  { kind: "service", label: "Services" },
  { kind: "vendor", label: "Vendors" },
];

const BG_STARS: [number, number, number, number][] = [
  [90, 180, 1.1, 0.5],
  [340, 150, 0.9, 0.4],
  [700, 130, 1.2, 0.45],
  [980, 200, 1, 0.4],
  [180, 470, 0.9, 0.4],
  [430, 610, 1, 0.35],
  [760, 600, 1.1, 0.4],
  [1130, 430, 0.9, 0.4],
  [60, 300, 0.8, 0.35],
  [1180, 250, 1, 0.4],
  [520, 110, 0.8, 0.3],
  [880, 470, 0.9, 0.35],
];

export function TwinViz({
  mode,
  viewW = VIEW_W,
  nodes,
  edges,
  states,
  matchSet = null,
  selected,
  blastSet,
  showBlast,
  fragility = null,
  liveHealth = null,
  trace = null,
  revealDepth = Infinity,
  includeIsolated = false,
  reserveBottomStart = 0,
  reserveBottomEnd = 0,
  onLabelBudget,
  onSelect,
  onHover,
  onLeave,
}: TwinVizProps): JSX.Element {
  const searching = matchSet !== null;
  const layout: LayoutResult = useMemo(() => {
    if (mode === "flow") return flowLayout(nodes, viewW);
    if (mode === "orbit") return orbitLayout(nodes, viewW);
    if (mode === "ring")
      return serviceRingLayout(nodes, edges, viewW, { includeIsolated });
    return observatoryLayout(nodes, viewW, reserveBottomStart, reserveBottomEnd);
  }, [
    mode,
    nodes,
    edges,
    viewW,
    includeIsolated,
    reserveBottomStart,
    reserveBottomEnd,
  ]);

  const positions = layout.positions;

  // P404.1: bundle the fan-in. Measured on the demo estate, 80 of 86
  // service->vendor edges converge on 5 vendors (max 24 to one), so
  // the remaining noise after the spacing pass is five dense sprays of
  // straight lines. Routing a target's edges through a shared trunk
  // moves the crossings INSIDE the bundle instead of across the canvas.
  //
  // Not applied in flow or ring: flow already separates by column and
  // its S-curves read cleanly, and the ring bows its chords toward the
  // centre for the same reason (P385). Bundling those would fight a
  // layout that has already solved it.
  const trunks = useMemo(
    () =>
      mode === "orbit" || mode === "observatory"
        ? computeTrunks(edges, positions)
        : new Map<string, { x: number; y: number }>(),
    [mode, edges, positions],
  );

  const sel = selected;
  const active = sel && showBlast ? blastSet : null;

  // P404: draw the labels that FIT, highest priority first.
  //
  // At 78 services down one column the labels overlapped so heavily
  // that none could be read -- overlapping text does not only ruin its
  // own label, it ruins its neighbours', so the cost of drawing one is
  // paid by the ones around it. Priority keeps the labels carrying the
  // most meaning: the inspected node, its blast set and search matches
  // never lose a label to an arbitrary neighbour.
  const visibleLabels = useMemo(
    () =>
      selectVisibleLabels(
        nodes.map((n) => {
          const p = positions[n.id] ?? { x: 0, y: 0 };
          let priority = 0;
          if (n.id === selected) priority = 100;
          else if (matchSet?.has(n.id)) priority = 80;
          else if (active?.has(n.id)) priority = 60;
          else if (n.kind === "channel" || n.kind === "ibs") priority = 20;
          // The label's own box, not the node's centre: it is offset
          // above or below the orb and anchored differently per layout,
          // and it is as wide as its text. Measuring the node instead
          // let a 200px name clear a 96px reach test and overlap anyway.
          const off = labelOffset(n, p, layout.centre, mode);
          return {
            id: n.id,
            x: p.x + off.x,
            y: p.y + off.y,
            priority,
            width: estimateLabelWidth(n.name),
            anchor: off.anchor,
          };
        }),
        {
          // Round 14 E3 -- a name is not drawn under another node's orb.
          orbs: nodes.map((n) => {
            const p = positions[n.id] ?? { x: 0, y: 0 };
            return { id: n.id, x: p.x, y: p.y, r: nodeRadius(n) };
          }),
        },
      ),
    [nodes, positions, selected, matchSet, active, layout.centre, mode],
  );

  // Which role a node plays in the trace, and whether the reveal has
  // reached it yet.
  //
  // "both" is not a tie to be broken -- it is CYCLE MEMBERSHIP, and it
  // matters: a node that is simultaneously your consequence and your
  // prerequisite cannot be recovered independently of you. P366 treats
  // exactly this as one restoration block. Collapsing it to "downstream"
  // (the first version of this) hid every prerequisite of a node on the
  // real estate cycle, which is the most interesting node to click.
  const traceOf = useCallback(
    (id: string): TraceRole | null => {
      if (!trace || !showBlast) return null;
      const d = trace.downstream.get(id);
      const u = trace.upstream.get(id);
      const down = d !== undefined && d <= revealDepth;
      const up = u !== undefined && u <= revealDepth;
      if (down && up) return "both";
      if (down) return "downstream";
      if (up) return "upstream";
      return null;
    },
    [trace, showBlast, revealDepth],
  );
  // A selection takes precedence over an active search: once a node
  // is clicked, show its blast mapping / connectivity normally
  // rather than keeping the search dim. The query stays in the box;
  // clearing the selection resumes the search highlight.
  const selecting = sel !== null;

  const edgeRecords = useMemo(() => {
    return edges
      .map((e) => {
        const a = positions[e.from_id];
        const b = positions[e.to_id];
        if (!a || !b) return null;
        const sFrom = states.get(e.from_id);
        const sTo = states.get(e.to_id);
        const cascade =
          (sFrom === "down" || sFrom === "degraded") &&
          (sTo === "down" || sTo === "degraded");
        const inBlast =
          active &&
          (e.from_id === sel || active.has(e.from_id)) &&
          (e.to_id === sel || active.has(e.to_id));
        // An edge belongs to the direction that lit its far end.
        const far = e.from_id === sel ? e.to_id : e.from_id;
        // An edge is a single line, so a cycle edge renders in the
        // consequence tone; the NODE rings carry the "both" reading.
        const dir =
          e.from_id === sel || e.to_id === sel || inBlast ? traceOf(far) : null;
        return { e, a, b, cascade, inBlast, dir };
      })
      .filter(
        (
          r,
        ): r is {
          e: TwinEdge;
          a: NodePos;
          b: NodePos;
          cascade: boolean;
          inBlast: boolean;
          dir: TraceRole | null;
        } => r !== null,
      );
  }, [edges, positions, states, sel, active, traceOf]);

  /* Round 11 C1 -- the hit boxes, computed once and shared by the
     rectangles that draw them and the resolver that reads them. Two
     copies of this geometry is how a click lands in one box and
     resolves against another. */
  /* E4 -- report the suppressed count upward once it settles. In an
     effect rather than during render: a parent that re-renders on it
     would otherwise be told again in the middle of being told. */
  useEffect(() => {
    onLabelBudget?.(nodes.length - visibleLabels.size);
  }, [onLabelBudget, nodes.length, visibleLabels]);

  const hitAreas = useMemo(() => {
    const out: {
      node: TwinNode;
      centre: { x: number; y: number };
      box: HitBox;
      radius: number;
      label: HitBox | null;
    }[] = [];
    for (const n of nodes) {
      const p = positions[n.id];
      if (!p) continue;
      const off = labelOffset(n, p, layout.centre, mode);
      const label = visibleLabels.has(n.id)
        ? { text: n.name, dx: off.x, dy: off.y, anchor: off.anchor }
        : null;
      out.push({
        node: n,
        centre: p,
        box: nodeHitBox(p, nodeRadius(n), label),
        // Round 14 E3 -- the shapes themselves, so a click resolves to
        // the node whose orb or name it is on.
        radius: nodeRadius(n),
        label: label ? labelBox(p, label) : null,
      });
    }
    return out;
  }, [nodes, positions, visibleLabels, layout.centre, mode]);

  /**
   * The node a click belongs to, in viewBox coordinates.
   *
   * The event arrives in SCREEN pixels and the boxes are in viewBox
   * units, so the point is converted through the SVG's own current
   * transform rather than by arithmetic on the viewBox -- the two
   * differ the moment `preserveAspectRatio` letterboxes, and a
   * resolver that is right only at one aspect ratio is worse than
   * none. Where the browser cannot give us the matrix (jsdom, and any
   * detached render) the rectangle that was clicked stands, which is
   * the behaviour this replaces.
   */
  const resolveClick = (ev: React.MouseEvent<SVGElement>, fallback: string) => {
    const svg = ev.currentTarget.ownerSVGElement;
    const ctm = svg?.getScreenCTM?.();
    if (!svg || !ctm || typeof svg.createSVGPoint !== "function") return fallback;
    const pt = svg.createSVGPoint();
    pt.x = ev.clientX;
    pt.y = ev.clientY;
    const local = pt.matrixTransform(ctm.inverse());
    return (
      nodeAtPoint(
        { x: local.x, y: local.y },
        hitAreas.map((h) => ({
          id: h.node.id,
          centre: h.centre,
          box: h.box,
          radius: h.radius,
          label: h.label,
        })),
      ) ?? fallback
    );
  };

  const dimmed = (id: string): boolean =>
    active !== null && id !== sel && !active.has(id);

  return (
    <svg
      data-testid="constellation-viz"
      data-mode={mode}
      className="constellation-viz"
      viewBox={`0 0 ${viewW} ${VIEW_H}`}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <filter id="cstGlowRed" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="cstGlowAmber" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="cstGlowBrass" x="-120%" y="-120%" width="340%" height="340%">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {mode === "observatory" && (
        <g opacity={0.5}>
          {BG_STARS.map(([x, y, r, o], i) => (
            <circle
              key={i}
              cx={x * (viewW / VIEW_W)}
              cy={y}
              r={r}
              fill="#C8A24A"
              opacity={o}
            />
          ))}
        </g>
      )}

      {mode === "orbit" && layout.centre && layout.radii && (
        <g>
          {(Object.entries(layout.radii) as [TwinNodeKind, number][]).map(([k, r]) => (
            <ellipse
              key={k}
              cx={layout.centre!.x}
              cy={layout.centre!.y}
              rx={r * (layout.xStretch ?? 1.32)}
              ry={r * 0.92}
              fill="none"
              stroke="rgba(200,162,74,0.13)"
              strokeWidth="1"
              strokeDasharray="3 6"
            />
          ))}
          <circle
            cx={layout.centre.x}
            cy={layout.centre.y}
            r="3"
            fill="rgba(200,162,74,0.5)"
          />
        </g>
      )}

      {mode === "ring" && layout.ring && (
        <g>
          <circle
            cx={layout.ring.centre.x}
            cy={layout.ring.centre.y}
            r={layout.ring.radius}
            fill="none"
            stroke="rgba(200,162,74,0.16)"
            strokeWidth="1"
            strokeDasharray="3 6"
          />
          {layout.ring.isolatedIds.length > 0 && (
            <text
              x={layout.ring.centre.x}
              y={layout.ring.centre.y + layout.ring.radius + 42}
              textAnchor="middle"
              fill="rgba(185,179,164,0.75)"
              fontSize="11"
              data-testid="constellation-ring-isolated-note"
            >
              {i18n.t("screens.twinViz.isolatedCount", {
                count: layout.ring.isolatedIds.length,
              })}
            </text>
          )}
        </g>
      )}

      {mode === "flow" &&
        COL_HEAD_LABELS.map((c) => (
          <text
            key={c.kind}
            x={flowColumns(viewW)[c.kind]}
            y="56"
            textAnchor="middle"
            className="constellation-colhead"
          >
            {c.label}
          </text>
        ))}

      <g>
        {edgeRecords.map((rec, i) => {
          let stroke = "rgba(200,162,74,0.13)";
          let width = 1;
          let dash: string | undefined = mode === "observatory" ? "3 5" : undefined;
          if (mode === "ring") {
            // The ring exists to show these edges; at the default
            // stroke they were barely visible against the stage.
            stroke = "rgba(200,162,74,0.42)";
            width = 1.2;
          }
          let glow: string | undefined;
          if (rec.cascade) {
            stroke = "rgba(226,87,74,0.55)";
            width = 1.8;
            dash = "6 5";
          }
          if (rec.inBlast) {
            stroke = "rgba(224,190,106,0.85)";
            width = 2;
            dash = undefined;
            glow = "url(#cstGlowBrass)";
          }
          // P385: consequence vs prerequisite are DIFFERENT questions and
          // must not look the same -- the whole MVB recovery regime rests
          // on the distinction. Encoded by hue AND dash, so it survives
          // colour-vision deficiency rather than relying on hue alone.
          if (rec.dir === "downstream" || rec.dir === "both") {
            stroke = "rgba(224,190,106,0.9)";
            width = 2;
            dash = undefined;
            glow = "url(#cstGlowBrass)";
          } else if (rec.dir === "upstream") {
            stroke = "rgba(244,237,223,0.85)";
            width = 1.8;
            dash = "5 4";
            glow = undefined;
          }
          // While a reveal is in flight, an edge the trace has not
          // reached yet stays dim even if it is in the blast set.
          const tracing = trace !== null && showBlast && Number.isFinite(revealDepth);
          const faded = tracing ? rec.dir === null : active !== null && !rec.inBlast;
          const trunk = trunks.get(rec.e.to_id);
          const d = trunk
            ? bundledPath(rec.a, rec.b, trunk)
            : edgePath(rec.a, rec.b, mode, layout.centre);
          return (
            <g key={i}>
              <path
                d={d}
                fill="none"
                stroke={stroke}
                strokeWidth={width}
                strokeDasharray={dash}
                filter={glow}
                opacity={selecting ? (faded ? 0.12 : 1) : searching ? 0.06 : 1}
              />
              {(rec.dir !== null || (trace === null && rec.inBlast)) && (
                // P91: a little orb travelling node-to-node along each
                // lit edge. Bounded by the selected node's reach so it
                // stays cheap. Staggered start so the orbs don't pulse
                // in lockstep.
                <circle
                  r={2.8}
                  fill={rec.dir === "upstream" ? "#F4EDDF" : "#F4E3B0"}
                  filter={rec.dir === "upstream" ? undefined : "url(#cstGlowBrass)"}
                >
                  <animateMotion
                    dur="1.5s"
                    begin={`-${(i % 5) * 0.3}s`}
                    repeatCount="indefinite"
                    // Upstream orbs run BACKWARDS along the path, toward
                    // the seed: a prerequisite flows in, a consequence
                    // flows out.
                    keyPoints={rec.dir === "upstream" ? "1;0" : undefined}
                    keyTimes={rec.dir === "upstream" ? "0;1" : undefined}
                    calcMode={rec.dir === "upstream" ? "linear" : undefined}
                    path={d}
                  />
                </circle>
              )}
            </g>
          );
        })}
      </g>

      {/* Round 10 A1 -- the areas that answer for a node, in a layer of
          their own BENEATH every node's visuals.

          Inline inside each node they worked, and then a neighbour's
          forgiving rectangle sat on top of an orb and stole its click:
          measured, orb hits fell 75 of 78 to 67 while box hits rose.
          In one layer underneath, an orb always answers for itself and
          a rectangle can only win where nothing is drawn -- the gap
          between a dot and its own name, which is exactly the canvas
          this is for. */}
      <g data-testid="constellation-hit-layer">
        {hitAreas.map(({ node: n, box }) => (
          <rect
            key={n.id}
            data-testid="constellation-node-hit"
            data-id={n.id}
            x={box.x}
            y={box.y}
            width={box.width}
            height={box.height}
            fill="transparent"
            style={{ cursor: "pointer" }}
            /* Round 11 C1 -- the rectangle a click LANDS on is not
               always the node a reader meant. Hit areas are the union
               of an orb and its name, so neighbours overlap, and SVG
               resolves an overlap by paint order: three of
               eighty-five selected somebody else. Paint order is
               arbitrary with respect to intent; distance is not, so
               the click is resolved to the nearest orb centre among
               the boxes that contain it. */
            onClick={(ev) => onSelect(resolveClick(ev, n.id))}
            onMouseMove={(ev) => onHover(n, ev, states.get(n.id) ?? "stable")}
            onMouseLeave={onLeave}
          />
        ))}
      </g>

      <g>
        {nodes.map((n) => {
          const p = positions[n.id];
          if (!p) return null;
          const st: NodeVisualState = states.get(n.id) ?? "stable";
          const r = nodeRadius(n);
          const isSel = n.id === sel;
          const nodeDir = traceOf(n.id);
          const tracingNodes =
            trace !== null && showBlast && Number.isFinite(revealDepth);
          const inBlast = tracingNodes
            ? nodeDir !== null
            : active !== null && active.has(n.id);
          const matched = searching && matchSet!.has(n.id);
          // P197 live lens: when it's active, dim every node that carries
          // no telemetry (non-service, or a service with no live reading)
          // so the telemetried services + their status rings stand out.
          const liveDim =
            liveHealth != null && !(n.kind === "service" && liveHealth.has(n.id));
          // Selection wins over search wins over the live-lens dim.
          const fade = selecting
            ? dimmed(n.id)
              ? 0.22
              : 1
            : searching
              ? matched
                ? 1
                : 0.1
              : liveDim
                ? 0.3
                : 1;
          const glow =
            st === "down"
              ? "url(#cstGlowRed)"
              : st === "degraded"
                ? "url(#cstGlowAmber)"
                : isSel || inBlast
                  ? "url(#cstGlowBrass)"
                  : undefined;
          const off = labelOffset(n, p, layout.centre, mode);
          return (
            <g
              key={n.id}
              data-testid="constellation-node"
              data-kind={n.kind}
              data-id={n.id}
              data-state={st}
              data-selected={isSel ? "true" : "false"}
              data-in-blast={inBlast ? "true" : "false"}
              data-match={matched ? "true" : "false"}
              style={{ cursor: "pointer", opacity: fade, transition: "opacity .25s" }}
              /* Round 11 C1 -- and the VISUALS resolve the same way.
                 Fixing only the hit layer left six of seventy-eight
                 still selecting a neighbour, measured with a real
                 mouse: a node's own group answers for its orb AND its
                 name, and a wide name laid over a neighbour's orb
                 hands that neighbour's click to itself. One resolver,
                 so every surface of a node agrees about whose click
                 it is. */
              onClick={(ev) => onSelect(resolveClick(ev, n.id))}
              onMouseMove={(ev) => onHover(n, ev, st)}
              onMouseLeave={onLeave}
            >
              {fragility?.get(n.id) && (
                // P167 fragility lens: a translucent aura sized by the
                // node's fragility ratio -- the SPOFs glow largest.
                // Rose when stressed (open findings) else brass.
                <circle
                  className="constellation-fragility-aura"
                  data-testid="constellation-fragility-aura"
                  data-id={n.id}
                  cx={p.x}
                  cy={p.y}
                  r={r + 5 + fragility.get(n.id)!.ratio * 26}
                  fill={
                    fragility.get(n.id)!.stress
                      ? "rgba(226,87,74,0.20)"
                      : "rgba(200,162,74,0.20)"
                  }
                  opacity={0.35 + fragility.get(n.id)!.ratio * 0.5}
                  filter={
                    fragility.get(n.id)!.stress
                      ? "url(#cstGlowRed)"
                      : "url(#cstGlowBrass)"
                  }
                />
              )}
              {nodeDir !== null && !isSel && (
                // P385: a direction ring so a node's ROLE in the trace
                // is readable without following its edge -- solid brass
                // for a consequence, dashed cream for a prerequisite.
                <circle
                  data-testid="constellation-trace-ring"
                  data-id={n.id}
                  data-direction={nodeDir}
                  cx={p.x}
                  cy={p.y}
                  r={r + 4}
                  fill="none"
                  stroke={
                    nodeDir === "upstream"
                      ? "rgba(244,237,223,0.9)"
                      : "rgba(224,190,106,0.95)"
                  }
                  strokeWidth={1.6}
                  strokeDasharray={nodeDir === "upstream" ? "3 3" : undefined}
                />
              )}
              {nodeDir === "both" && !isSel && (
                // Cycle member: carries the consequence ring above AND a
                // dashed prerequisite ring outside it, so "we cannot
                // recover independently of each other" is visible.
                <circle
                  data-testid="constellation-trace-ring"
                  data-id={n.id}
                  data-direction="upstream"
                  cx={p.x}
                  cy={p.y}
                  r={r + 7}
                  fill="none"
                  stroke="rgba(244,237,223,0.9)"
                  strokeWidth={1.4}
                  strokeDasharray="3 3"
                />
              )}
              {liveHealth && n.kind === "service" && liveHealth.get(n.id) && (
                // P197 live-telemetry lens: a status ring on each
                // telemetried service -- down/degraded glow (red/brass),
                // healthy/unknown a calm ring. The live board over the topology.
                <circle
                  data-testid="constellation-live-ring"
                  data-id={n.id}
                  data-live-status={liveHealth.get(n.id)}
                  cx={p.x}
                  cy={p.y}
                  r={r + 5}
                  fill="none"
                  stroke={LIVE_RING[liveHealth.get(n.id)!].stroke}
                  strokeWidth={2.5}
                  filter={LIVE_RING[liveHealth.get(n.id)!].glow}
                />
              )}
              {matched && !selecting && (
                // P92: brass highlight ring around a search match.
                // Suppressed once a node is selected -- the blast
                // visuals take over (P93).
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={r + 6}
                  fill="none"
                  stroke="#E0BE6A"
                  strokeWidth={2}
                  filter="url(#cstGlowBrass)"
                />
              )}
              {(isSel || inBlast) && (
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={r + 7}
                  fill="none"
                  stroke={isSel ? "#E0BE6A" : "rgba(224,190,106,0.6)"}
                  strokeWidth={isSel ? 2 : 1.4}
                />
              )}
              {n.kind === "ibs" && n.service_class === "internal" && (
                /* P429.6 -- a Critical Internal Function: its own teal
                   ring, never the gold reserved for an IBS. */
                <circle
                  data-testid="constellation-cif-ring"
                  cx={p.x}
                  cy={p.y}
                  r={r + 3.5}
                  fill="none"
                  stroke="rgba(95,179,166,0.85)"
                  strokeWidth="1.4"
                />
              )}
              {n.kind === "ibs" && n.mvb_flag && n.service_class !== "internal" && (
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={r + 3.5}
                  fill="none"
                  stroke="rgba(200,162,74,0.4)"
                  strokeWidth="1"
                  strokeDasharray="2 2.5"
                />
              )}
              <circle
                cx={p.x}
                cy={p.y}
                r={r}
                fill={STATE_FILL[st]}
                stroke={STATE_STROKE[st]}
                strokeWidth={n.kind === "ibs" ? 2 : 1.5}
                filter={glow}
              />
              {n.kind === "vendor" && (
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={r * 0.4}
                  fill={
                    st === "stable" ? "rgba(200,162,74,0.55)" : "rgba(255,255,255,0.55)"
                  }
                />
              )}
              {visibleLabels.has(n.id) && (
                <text
                  className={
                    "constellation-nlab" + (st === "stable" && !matched ? " dim" : "")
                  }
                  data-testid="constellation-node-label"
                  x={p.x + off.x}
                  y={p.y + off.y}
                  textAnchor={off.anchor}
                  stroke="#0d141e"
                  strokeWidth="3"
                  style={{ paintOrder: "stroke" }}
                >
                  {n.name}
                </text>
              )}
            </g>
          );
        })}
      </g>

      {/* Visually-hidden text for tests to assert on the kind label. */}
      <title>
        {i18n.t("screens.twinViz.canvasTitle", {
          mode,
          count: nodes.length,
        })}
      </title>
    </svg>
  );
}

function edgePath(
  a: NodePos,
  b: NodePos,
  mode: ConstellationMode,
  centre?: NodePos,
): string {
  if (mode === "flow") {
    const mx = (a.x + b.x) / 2;
    return `M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`;
  }
  if (mode === "ring" && centre) {
    // Chord bowed toward the centre. Curvature scales with how far
    // apart the endpoints sit on the rim, so short hops hug the rim
    // and long ones cut across -- which is what makes a dense ring
    // readable instead of a hairball.
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const span = Math.hypot(a.x - b.x, a.y - b.y);
    const rim = Math.hypot(mx - centre.x, my - centre.y) || 1;
    const pull = Math.min(0.9, span / (rim * 2 + span));
    const qx = mx + (centre.x - mx) * pull;
    const qy = my + (centre.y - my) * pull;
    return `M ${a.x} ${a.y} Q ${qx} ${qy}, ${b.x} ${b.y}`;
  }
  return `M ${a.x} ${a.y} L ${b.x} ${b.y}`;
}

interface LabelOffset {
  x: number;
  y: number;
  anchor: "start" | "middle" | "end";
}

function labelOffset(
  n: TwinNode,
  p: NodePos,
  centre: NodePos | undefined,
  mode: ConstellationMode,
): LabelOffset {
  const r = nodeRadius(n);
  if ((mode === "orbit" || mode === "ring") && centre) {
    const dx = p.x - centre.x;
    const dy = p.y - centre.y;
    const len = Math.hypot(dx, dy) || 1;
    // Ring labels sit OUTSIDE the rim and further out than orbit's, so
    // 37+ names around one perimeter do not overprint each other the
    // way they do when they hug the node.
    const dist = mode === "ring" ? r + 20 : r + 12;
    return {
      x: (dx / len) * dist,
      y: (dy / len) * dist,
      anchor: dx >= 0 ? "start" : "end",
    };
  }
  if (mode === "flow") {
    const right = n.kind === "channel" || n.kind === "ibs";
    return { x: right ? r + 12 : -(r + 12), y: 4, anchor: right ? "start" : "end" };
  }
  // observatory -- below by default; vendor + long-name service above
  const above = n.kind === "vendor" || (n.name.length > 14 && n.kind === "service");
  return { x: 0, y: above ? -(r + 9) : r + 17, anchor: "middle" };
}

/** Helper export so tests + the inspector can label the kind. */
export { KIND_LABEL };
